import type RootStore from "stores/RootStore";
import type { RouteType } from "types/RouteType";
import type BillingCapabilitiesStore from "./BillingCapabilitiesStore";

/**
 * The route gate: billing appears only on an instance configured for it.
 *
 * Two halves, and both are needed:
 *
 *   - `beforeEnter` asks the API (once, cached) and refuses the route when the
 *     instance is not configured. This is the one that actually protects: a
 *     hidden route is still reachable by typing the URL.
 *   - `hidden` keeps it out of the nav and the page-search so nobody is offered
 *     a module that will bounce them. It is a GETTER, not a snapshot — see
 *     gateBillingRoutes.
 *
 * Deliberately NOT a build-time flag. The same web image is deployed to every
 * instance, so the answer has to come from the running API's config, not from
 * what happened to be set when the bundle was built.
 */

/**
 * The live capabilities store, for the `hidden` getter.
 *
 * Route objects are module-level constants built at import time — long before a
 * RootStore exists — so they cannot close over one. BillingCapabilitiesStore
 * registers itself here from its constructor instead (the app store manager
 * builds exactly one, with the RootStore in hand).
 */
let liveCapabilities: BillingCapabilitiesStore | null = null;

/** Called by BillingCapabilitiesStore's constructor. */
export const registerBillingCapabilities = (
  store: BillingCapabilitiesStore | null
): void => {
  liveCapabilities = store;
};

/** The registered store. Exported for tests and for the getter's default. */
export const billingCapabilities = (): BillingCapabilitiesStore | null =>
  liveCapabilities;

const capabilitiesStore = (rootStore: RootStore): BillingCapabilitiesStore | null =>
  (rootStore as unknown as { billingCapabilitiesViewStore?: BillingCapabilitiesStore })
    .billingCapabilitiesViewStore ?? null;

/**
 * Route guard. Returns false — cancelling navigation — when billing is not
 * configured, and sends the user somewhere real rather than leaving them on a
 * dead route.
 */
export const billingBeforeEnter = async ({
  rootStore,
}: {
  rootStore: RootStore;
}): Promise<boolean> => {
  const store = capabilitiesStore(rootStore);
  if (!store) return false;

  const capabilities = await store.load();
  if (!capabilities.enabled) {
    // Fail closed AND fail visibly: silently swallowing the navigation looks
    // like a broken link.
    rootStore.notificationStore?.addNotification?.({
      message: store.disabledReason,
      options: { variant: "warning" },
    });
    rootStore.routerStore?.goTo?.("Home");
    return false;
  }
  return true;
};

/**
 * Apply the gate to every route in a billing route list.
 *
 * `hidden` is defined as a GETTER over the capabilities store, not copied off
 * the source route. That is what makes it honest: the nav tree and the omnibar
 * (@intrasync/stores AppLayoutStore — `quickNavigationItems` and
 * `omnibarNavigableRoutes`) are MobX computeds that re-read `route.hidden` on
 * every recomputation, and RouteManager assigns the route list into an
 * observable field, so MobX converts these plain route objects deeply and turns
 * this getter into a computed. Reading `capabilities.enabled` inside it
 * therefore SUBSCRIBES the nav to the capability report: the moment it lands,
 * the nav re-renders with billing in it. (Verified against mobx 6 —
 * `isComputedProp(routesList[0], "hidden") === true`.) The route objects are
 * never spread again downstream (routesList.tsx spreads arrays, rootStoreConfig
 * keys them, RootStore passes `Object.values(...)`), so the getter survives to
 * the store.
 *
 * VISIBLE-ONCE-CONFIRMED, and the tradeoff is deliberate: `enabled` is false
 * both before the report arrives and when billing is off, so billing is hidden
 * until the API confirms it is configured. On a configured instance that means
 * the nav is missing Billing for the few hundred ms between boot and the
 * capability response — the getter flips it in as soon as the answer lands.
 * The alternative (visible while unknown) would flash a Billing entry on EVERY
 * unconfigured instance and then withdraw it, offering a link that bounces to
 * Home with a warning. A brief absence on the instances that have billing beats
 * a brief lie on the ones that do not.
 */
export const gateBillingRoutes = (
  routes: RouteType[],
  resolveCapabilities: () => BillingCapabilitiesStore | null = billingCapabilities
): RouteType[] =>
  routes.map((route) => ({
    ...route,
    requiresAuth: true,
    // A route that declares itself hidden stays hidden; otherwise visibility is
    // whatever the capability report says right now.
    get hidden(): boolean {
      if (route.hidden === true) return true;
      return resolveCapabilities()?.enabled !== true;
    },
    beforeEnter: async (context) => {
      const allowed = await billingBeforeEnter(context);
      if (!allowed) return false;
      // Preserve a route's own guard if it has one.
      return route.beforeEnter ? route.beforeEnter(context) : true;
    },
  }));
